import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './ExperimentsTable.css';
import Select from 'react-select';
import { auth } from "../../config/firebase";
import { onAuthStateChanged } from 'firebase/auth';

const API_BASE_URL = 'http://localhost:5001';

const ExperimentsTable = () => {
    const navigate = useNavigate();
    // const [lastFetchTime, setLastFetchTime] = useState(null);
    const [experiments, setExperiments] = useState({});
    const [metadata, setMetadata] = useState({});
    const [allScenarios, setAllScenarios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentUserId, setCurrentUserId] = useState(null);


    // State for handling experiment selection
    const [experimentOptions, setExperimentOptions] = useState([]);
    const [selectedExperiments, setSelectedExperiments] = useState([]);
    const [filteredScenarios, setFilteredScenarios] = useState([]);
    const [favoriteScenarios, setFavoriteScenarios] = useState([]);
    const [showCompareButton, setShowCompareButton] = useState(false);
    const [selectedScenarioKeys, setSelectedScenarioKeys] = useState([]);
    const [compareMode, setCompareMode] = useState(false);
    const [originalFilteredScenarios, setOriginalFilteredScenarios] = useState([]);
    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState('');

    // Auth state monitoring
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                setCurrentUserId(user.uid);
            }
            else {
                setCurrentUserId(null);
                navigate('/');
            }
        });

        return () => unsubscribe();
    }, [navigate]);

    // Fetch data when user ID changes
    useEffect(() => {
        const fetchData = async () => {
            if (!currentUserId) return;
            setLoading(true);
            // Inside your useEffect for fetching data:
            try {

                // Get the user's experiments (for dropdown)
                const experimentsRes = await fetch(`${API_BASE_URL}/users/${currentUserId}/experiments`);
                if (!experimentsRes.ok) throw new Error(await experimentsRes.text());
                const experimentsData = await experimentsRes.json();
                const userExperiments = experimentsData.available_experiments || {};
                setExperiments(userExperiments);

                // Create options for the experiment selector dropdown
                const options = Object.entries(userExperiments).map(([id, info]) => ({
                    value: id,
                    label: info.name || id,
                    experimentId: id
                }));
                setExperimentOptions(options);
                const metadataRes = await fetch(`${API_BASE_URL}/users/${currentUserId}/metadata`);
                if (!metadataRes.ok) throw new Error(await metadataRes.text());
                const payload = await metadataRes.json();
                if (!payload.success) {
                    throw new Error('Failed to fetch metadata');
                }
                const nestedMeta = payload.data;

                // Process the results
                const flatScenarios = [];
                const metadataByScenario = {};

                Object.entries(nestedMeta).forEach(([expId, expObj]) => {
                    const expName = expObj.name || expId;
                    const scenarios = expObj.metadata || {};
                    // Access scenarios within this experiment
                    Object.entries(scenarios).forEach(([scenarioId, scenarioMeta]) => {
                        const key = `${expId}|${scenarioId}`;
                        metadataByScenario[key] = scenarioMeta;

                        flatScenarios.push({
                            userId: currentUserId,
                            experimentId: expId,
                            experimentName: expName,
                            scenarioId,
                            scenarioName: scenarioId,
                            results: { metadata: scenarioMeta }
                        });

                    });
                });

                setAllScenarios(flatScenarios);
                setFilteredScenarios(flatScenarios);
                setMetadata(metadataByScenario);

            } catch (err) {
                console.error('Error fetching data:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [currentUserId]);

    const compareModePrevRef = useRef(compareMode);

    // Filter scenarios when selected experiments change
    useEffect(() => {
        // Check if compare mode changed
        if (compareMode && !compareModePrevRef.current) {
            // Compare mode was just turned on
        } else if (!compareMode && compareModePrevRef.current) {
            // Compare mode was just turned off
        }

        // Update the ref
        compareModePrevRef.current = compareMode;

        // Handle experiment filtering
        if (selectedExperiments.length === 0) {
            setFilteredScenarios(allScenarios);

        } else {
            // Filter scenarios by selected experiments
            const selectedIds = selectedExperiments.map(exp => exp.value);

            const filtered = allScenarios.filter(scenario =>
                selectedIds.includes(scenario.experimentId)
            );

            setFilteredScenarios(filtered);
        }
    }, [selectedExperiments, allScenarios]);

    useEffect(() => {
        if (currentUserId) {
            fetchFavories();
        }
    }, [currentUserId]);

    // Handle experiment selection change
    const handleExperimentChange = (selected) => {
        setSelectedExperiments(selected || []);
        setSortField(null);
        setSortDirection('');
    };

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        }
        else {
            setSortField(field);
            setSortDirection('');
        }
    };

    const getSortedData = () => {
        if (!sortField) return filteredScenarios;

        return [...filteredScenarios].sort((a, b) => {
            const keyA = `${a.experimentId}|${a.scenarioId}`;
            const keyB = `${b.experimentId}|${b.scenarioId}`;
            const dataA = metadata[keyA] || {};
            const dataB = metadata[keyB] || {};

            let valA, valB;
            switch (sortField) {
                case 'capex':
                    valA = dataA.project_capex || 0;
                    valB = dataB.project_capex || 0;
                    break;
                case 'opex':
                    valA = dataA.project_opex || 0;
                    valB = dataB.project_opex || 0;
                    break;
                case 'chargerUtil':
                    valA = getAverageChargerUtilization(dataA.chargers) || 0;
                    valB = getAverageChargerUtilization(dataB.chargers) || 0;
                    break;
                case 'vehicleUtil':
                    const statusesA = getAverageVehicleStatuses(dataA.vehicles);
                    const statusesB = getAverageVehicleStatuses(dataB.vehicles);
                    valA = statusesA.operating || 0;
                    valB = statusesB.operating || 0;
                    break;
                default:
                    return 0;
            }
            if (sortDirection === 'asc') {
                return valA - valB;
            } else {
                return valB - valA;
            }
        });
    };

    const handleToggleFavorite = async (experimentId, scenarioId) => {
        const scenarioKey = `${experimentId}|${scenarioId}`;
        const isFavorite = favoriteScenarios.includes(scenarioKey);

        try {
            if (isFavorite) {
                const response = await fetch(`${API_BASE_URL}/users/${currentUserId}/favorites/${experimentId}/scenarios/${scenarioId}`, {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        experiment_id: experimentId,
                        scenario_id: scenarioId
                    }),
                });

                if (!response.ok) {
                    throw new Error('Failed to remove favorite');
                }

                const data = await response.json();
                if (data.success) {
                    setFavoriteScenarios(prevFavs => prevFavs.filter(key => key !== scenarioKey));
                    showNotification('Removed from favorites', 'success');
                } else {
                    throw new Error(data.message || 'Failed to remove favorite');
                }
            } else {
                const response = await fetch(`${API_BASE_URL}/users/${currentUserId}/favorites`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        experiment_id: experimentId,
                        scenario_id: scenarioId
                    }),
                });

                if (!response.ok) {
                    throw new Error('Failed to add favorite');
                }

                const data = await response.json();
                if (data.success) {
                    setFavoriteScenarios(prevFavs => [...prevFavs, scenarioKey]);
                    showNotification('Added to favorites', 'success');
                } else {
                    throw new Error(data.message || 'Failed to add favorite');
                }
            }
        } catch (error) {
            console.error('Error toggling favorite:', error);
            showNotification(error.message, 'error');
        }
    };

    const handleScenarioSelection = (scenarioKey) => {
        setSelectedScenarioKeys(prevSelected => {
            let newSelected;
            if (prevSelected.includes(scenarioKey)) {
                newSelected = prevSelected.filter(key => key !== scenarioKey);
            }
            else {
                newSelected = [...prevSelected, scenarioKey];
            }

            setShowCompareButton(newSelected.length >= 2);
            return newSelected;
        });
    };

    const clearSelections = () => {
        setSelectedScenarioKeys([]);
        setShowCompareButton(false);

        if (compareMode) {
            exitCompareMode();
        }
    };


    const showNotification = (message, type = 'success') => {
        // Create notification element
        const notification = document.createElement('div');
        notification.className = `notification ${type}-notification`;
        notification.textContent = message;

        // Add icon based on type
        const icon = document.createElement('span');
        icon.className = 'notification-icon';
        icon.innerHTML = type === 'success' ? '✓' : type === 'error' ? '✕' : 'ℹ';
        notification.insertBefore(icon, notification.firstChild);

        // Add to DOM
        document.body.appendChild(notification);

        // Show with animation
        setTimeout(() => {
            notification.classList.add('show');

            // Hide and remove after delay
            setTimeout(() => {
                notification.classList.remove('show');
                setTimeout(() => {
                    document.body.removeChild(notification);
                }, 300);
            }, 2500);
        }, 10);
    };

    const fetchFavories = async () => {
        if (!currentUserId) return;

        try {
            const response = await fetch(`${API_BASE_URL}/users/${currentUserId}/favorites`);

            if (!response.ok) {
                console.error('Failed to fetch favorites');
                return;
            }

            const data = await response.json();
            if (data.success && data.favorites) {
                const favoriteKeys = [];
                Object.entries(data.favorites).forEach(([expId, experiment]) => {
                    if (experiment.scenarios) {
                        Object.keys(experiment.scenarios).forEach(scenarioId => {
                            favoriteKeys.push(`${expId}|${scenarioId}`);
                        });
                    }
                });
                setFavoriteScenarios(favoriteKeys);
            }
        } catch (error) {
            console.error('Error fetching favorites:', error);
        }
    };

    // Helper functions for processing vehicle and charger data
    const getAverageVehicleStatuses = (vehicleData) => {
        if (!vehicleData) return { idle: 0, charging: 0, operating: 0 };
        const vehicles = Object.entries(vehicleData).filter(([key]) => !key.startsWith('metadata'));
        if (vehicles.length === 0) return { idle: 0, charging: 0, operating: 0 };

        const totals = vehicles.reduce((sum, [, data]) => ({
            idle: sum.idle + (data.idle_status_percentage || 0),
            charging: sum.charging + (data.charging_status_percentage || 0),
            operating: sum.operating + (data.operation_status_percentage || 0)
        }), { idle: 0, charging: 0, operating: 0 });

        return {
            idle: totals.idle / vehicles.length,
            charging: totals.charging / vehicles.length,
            operating: totals.operating / vehicles.length
        };
    };

    const getAverageChargerUtilization = (chargerData) => {
        if (!chargerData) return 0;
        const chargers = Object.entries(chargerData).filter(([key]) => !key.startsWith('metadata'));
        if (chargers.length === 0) return 0;
        return chargers.reduce((sum, [, data]) => sum + (data.utilization || 0), 0) / chargers.length;
    };

    if (loading) return <div className="loading-container">Loading experiments...</div>;
    if (error) return <div className="error-container">Error: {error}</div>;

    // Check if we have any data at all
    const hasExperiments = Object.keys(experiments).length > 0;

    const handleCompareScenarios = () => {
        if (selectedScenarioKeys.length < 2) {
            showNotification('Please select at least 2 scenarios to compare', 'warning');
            return;
        }

        if (!compareMode) {
            setOriginalFilteredScenarios(filteredScenarios);
        }

        const selectedScenarios = filteredScenarios.filter(scenario => {
            const scenarioKey = `${scenario.experimentId}|${scenario.scenarioId}`;
            return selectedScenarioKeys.includes(scenarioKey);
        });

        setFilteredScenarios(selectedScenarios);
        setCompareMode(true);
        showNotification(`Comparing ${selectedScenarioKeys.length} selected scenarios`, `info`);
    };

    const exitCompareMode = () => {
        setFilteredScenarios(originalFilteredScenarios);
        setCompareMode(false);
        setSortField(null);
        setSortDirection('');
    }

    return (
        <div className="experiments-table-container">
            <h1>COMPARATIVE ANALYSIS</h1>

            {!hasExperiments && !loading ? (
                <div className="no-data-message">
                    <p>No experiments found. Please upload scenarios first.</p>
                    <button onClick={() => navigate('/home')}>
                        Go to Upload Page
                    </button>
                </div>
            ) : (
                <>
                    <div className="selector-box">
                        <h2>Select Experiments To View</h2>
                        <Select
                            isMulti
                            name="experiments"
                            options={experimentOptions}
                            className="basic-multi-select"
                            classNamePrefix="select"
                            onChange={handleExperimentChange}
                            value={selectedExperiments}
                            placeholder="Select experiments to display (leave empty to show all)"
                        />
                    </div>
                    {compareMode && (
                        <div className="compare-mode-banner">
                            <div className="compare-mode-info">
                                <span>Comparing {filteredScenarios.length} scenarios</span>
                            </div>
                            <button
                                className="exit-compare-button"
                                onClick={exitCompareMode}
                            >
                                Exit Comparison Mode
                            </button>
                        </div>
                    )}
                    <div className="selection-controls">
                        <div className="selection-info">
                            {selectedScenarioKeys.length > 0 && (
                                <span>{selectedScenarioKeys.length} scenario{selectedScenarioKeys.length !== 1 ? 's' : ''} selected</span>
                            )}
                        </div>
                        <div className="selection-actions">
                            {selectedScenarioKeys.length > 0 && (
                                <button
                                    className="clear-selection-button"
                                    onClick={clearSelections}
                                >
                                    Clear Selection
                                </button>
                            )}
                            {showCompareButton && (
                                <button
                                    className="compare-button"
                                    onClick={handleCompareScenarios}
                                >
                                    Compare Selected Scenarios
                                </button>
                            )}
                        </div>
                    </div>

                    {filteredScenarios.length > 0 ? (
                        <>
                            <SummaryStats metadata={metadata} scenarios={filteredScenarios}
                                getAverageVehicleStatuses={getAverageVehicleStatuses}
                                getAverageChargerUtilization={getAverageChargerUtilization} />

                            <table className="experiments-table">
                                <thead>
                                    <tr>
                                        <th className="select-column">Select</th>
                                        <th className="favorite-column">Favorite</th>
                                        <th>Experiment: Scenario</th>
                                        <th className="sortable-column">Project CAPEX (k$)
                                            <div className="sort-controls">
                                                <button className={`sort-arrow ${sortField === 'capex' && sortDirection === 'asc' ? 'active' : ''}`}
                                                    onClick={() => { handleSort('capex'); setSortDirection('asc'); }}
                                                    aria-label="Sort CAPEX ascending">
                                                    ▲
                                                </button>
                                                <button
                                                    className={`sort-arrow ${sortField === 'capex' && sortDirection === 'desc' ? 'active' : ''}`}
                                                    onClick={() => { handleSort('capex'); setSortDirection('desc'); }}
                                                    aria-label="Sort CAPEX descending"
                                                >
                                                    ▼
                                                </button>
                                            </div>
                                        </th>
                                        <th className="sortable-column">Project OPEX (k$)
                                            <div className="sort-controls">
                                                <button
                                                    className={`sort-arrow ${sortField === 'opex' && sortDirection === 'asc' ? 'active' : ''}`}
                                                    onClick={() => { handleSort('opex'); setSortDirection('asc'); }}
                                                    aria-label="Sort OPEX ascending"
                                                >
                                                    ▲
                                                </button>
                                                <button
                                                    className={`sort-arrow ${sortField === 'opex' && sortDirection === 'desc' ? 'active' : ''}`}
                                                    onClick={() => { handleSort('opex'); setSortDirection('desc'); }}
                                                    aria-label="Sort OPEX descending"
                                                >
                                                    ▼
                                                </button>
                                            </div>
                                        </th>
                                        <th className="sortable-column">Charger Utilization (%)
                                            <div className="sort-controls">
                                                <button
                                                    className={`sort-arrow ${sortField === 'chargerUtil' && sortDirection === 'asc' ? 'active' : ''}`}
                                                    onClick={() => { handleSort('chargerUtil'); setSortDirection('asc'); }}
                                                    aria-label="Sort Charger Utilization ascending"
                                                >
                                                    ▲
                                                </button>
                                                <button
                                                    className={`sort-arrow ${sortField === 'chargerUtil' && sortDirection === 'desc' ? 'active' : ''}`}
                                                    onClick={() => { handleSort('chargerUtil'); setSortDirection('desc'); }}
                                                    aria-label="Sort Charger Utilization descending"
                                                >
                                                    ▼
                                                </button>
                                            </div>
                                        </th>
                                        <th className="sortable-column">Vehicle Utilization (%)
                                            <div className="sort-controls">
                                                <button
                                                    className={`sort-arrow ${sortField === 'vehicleUtil' && sortDirection === 'asc' ? 'active' : ''}`}
                                                    onClick={() => { handleSort('vehicleUtil'); setSortDirection('asc'); }}
                                                    aria-label="Sort Vehicle Utilization ascending"
                                                >
                                                    ▲
                                                </button>
                                                <button
                                                    className={`sort-arrow ${sortField === 'vehicleUtil' && sortDirection === 'desc' ? 'active' : ''}`}
                                                    onClick={() => { handleSort('vehicleUtil'); setSortDirection('desc'); }}
                                                    aria-label="Sort Vehicle Utilization descending"
                                                >
                                                    ▼
                                                </button>
                                            </div>
                                        </th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {getSortedData().map((scenario) => {
                                        const scenarioMetaKey = `${scenario.experimentId}|${scenario.scenarioId}`;
                                        const scenarioMeta = metadata[scenarioMetaKey] || {};
                                        const vehicleStatuses = getAverageVehicleStatuses(scenarioMeta.vehicles);
                                        const avgChargerUtil = getAverageChargerUtilization(scenarioMeta.chargers);
                                        const isFavorite = favoriteScenarios.includes(scenarioMetaKey);
                                        const isSelected = selectedScenarioKeys.includes(scenarioMetaKey);

                                        return (
                                            <tr key={`${scenario.experimentId}-${scenario.scenarioId}`}
                                                className={isSelected ? 'selected-row' : ''}>
                                                <td className="select-column">
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => handleScenarioSelection(scenarioMetaKey)}
                                                        aria-label={`Select ${scenario.scenarioName}`}
                                                    />
                                                </td>
                                                <td className="favorite-column">
                                                    <button className={`favorite-button ${isFavorite ? 'favorite-active' : ''}`}
                                                        onClick={() => handleToggleFavorite(scenario.experimentId, scenario.scenarioId)}
                                                        aria-label={isFavorite ? 'Added to favorites' : 'Add to favorites'}
                                                    >
                                                        {isFavorite ? '★' : '☆'}
                                                    </button>
                                                </td>
                                                <td>{scenario.experimentName}: {scenario.scenarioName}</td>
                                                <td>{scenarioMeta.project_capex ? (scenarioMeta.project_capex / 1000).toFixed(2) : 'N/A'}</td>
                                                <td>{scenarioMeta.project_opex ? (scenarioMeta.project_opex / 1000).toFixed(2) : 'N/A'}</td>
                                                <td>{avgChargerUtil ? (avgChargerUtil * 100).toFixed(2) : 'N/A'}%</td>
                                                <td>
                                                    <div>Operating: {vehicleStatuses.operating ? (vehicleStatuses.operating * 100).toFixed(2) : '0'}%</div>
                                                    <div>Charging: {vehicleStatuses.charging ? (vehicleStatuses.charging * 100).toFixed(2) : '0'}%</div>
                                                    <div>Idle: {vehicleStatuses.idle ? (vehicleStatuses.idle * 100).toFixed(2) : '0'}%</div>
                                                </td>
                                                <td>
                                                    <div className="view-details">
                                                        <button className="planning" onClick={() => navigate(`/planning/${currentUserId}/${scenario.experimentId}/${scenario.scenarioId}`)}>
                                                            Planning
                                                        </button>
                                                        <button className="operation" onClick={() => navigate(`/operation/${currentUserId}/${scenario.experimentId}/${scenario.scenarioId}`)}>
                                                            Operation
                                                        </button>
                                                    </div>

                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </>
                    ) : (
                        <div className="no-data-message">No scenarios available for the selected experiments.</div>
                    )}
                </>
            )}
        </div>
    );
};

const SummaryStats = ({ metadata, scenarios, getAverageVehicleStatuses, getAverageChargerUtilization }) => {
    const stats = React.useMemo(() => {
        if (!Object.keys(metadata).length || scenarios.length === 0) {
            
            return null;
        }

        let minCapexId, minOpexId, maxChargerUtilId, maxVehicleUtilId;
        let maxCapexId, maxOpexId, minChargerUtilId, minVehicleUtilId;
        let minCapex = Infinity, minOpex = Infinity;
        let maxCapex = -Infinity, maxOpex = -Infinity;
        let maxChargerUtil = -Infinity, maxVehicleUtil = -Infinity;
        let minChargerUtil = Infinity, minVehicleUtil = Infinity;

        // Arrays to store total charger and vehicle counts for each scenario
        const totalChargerCounts = [];
        const scenarioAvgVehicleUtils = [];

        // Find scenarios with min/max values
        for (const scenario of scenarios) {
            const scenarioKey = `${scenario.experimentId}|${scenario.scenarioId}`;
            const data = metadata[scenarioKey];
            if (!data) {
                continue;
            }
            if (data.project_capex) {
                if (data.project_capex && data.project_capex < minCapex) {
                    minCapex = data.project_capex;
                    minCapexId = scenarioKey;
                }
                if (data.project_capex && data.project_capex > maxCapex) {
                    maxCapex = data.project_capex;
                    maxCapexId = scenarioKey;
                }
            }

            if (data.project_opex) {
                if (data.project_opex && data.project_opex < minOpex) {
                    minOpex = data.project_opex;
                    minOpexId = scenarioKey;
                }
                if (data.project_opex && data.project_opex > maxOpex) {
                    maxOpex = data.project_opex;
                    maxOpexId = scenarioKey;
                }
            }

            // Get charger utilization and count total chargers
            let totalChargersInScenario = 0;
            if (data.chargers) {
                Object.entries(data.chargers).forEach(([chargerKey, chargerData]) => {
                    if (chargerKey === 'metadata') return;

                    totalChargersInScenario++;

                    if (typeof chargerData.utilization === 'number') {
                        if (chargerData.utilization > maxChargerUtil) {
                            maxChargerUtil = chargerData.utilization;
                            maxChargerUtilId = scenarioKey;
                        }
                        if (chargerData.utilization < minChargerUtil) {
                            minChargerUtil = chargerData.utilization;
                            minChargerUtilId = scenarioKey;
                        }
                    }
                });

                // Add the count to our array if we found chargers
                if (totalChargersInScenario > 0) {
                    totalChargerCounts.push(totalChargersInScenario);
                }
            }

            // Get average vehicle utilization for the scenario
            const vehicleStatuses = getAverageVehicleStatuses(data.vehicles);
            const avgOperatingUtil = vehicleStatuses.operating;
            if (avgOperatingUtil > 0) {
                scenarioAvgVehicleUtils.push({
                    scenarioKey,
                    util: avgOperatingUtil
                });

                // Compare with min/max
                if (avgOperatingUtil > maxVehicleUtil) {
                    maxVehicleUtil = avgOperatingUtil;
                    maxVehicleUtilId = scenarioKey;
                }
                if (avgOperatingUtil < minVehicleUtil) {
                    minVehicleUtil = avgOperatingUtil;
                    minVehicleUtilId = scenarioKey;
                }
            }

        }

        return {
            minCapex,
            minCapexId,
            minOpex,
            minOpexId,
            maxCapex,
            maxCapexId,
            maxOpex,
            maxOpexId,
            maxChargerUtil: maxChargerUtil === -Infinity ? 0 : maxChargerUtil * 100,
            maxChargerUtilId,
            minChargerUtil: minChargerUtil === Infinity ? 0 : minChargerUtil * 100,
            minChargerUtilId,
            maxVehicleUtil: maxVehicleUtil === -Infinity ? 0 : maxVehicleUtil * 100,
            maxVehicleUtilId,
            minVehicleUtil: minVehicleUtil === Infinity ? 0 : minVehicleUtil * 100,
            minVehicleUtilId
        };
    }, [metadata, scenarios]);

    if (!stats) return null;

    return (
        <div className="summary-stats">
            <div className="stat-item">
                <div className="stat-info">
                    <div className="stat-label">Min CAPEX</div>
                    <div className="stat-value">
                        ${(stats.minCapex / 1000).toFixed(2)}k
                    </div>
                    <div className="stat-value-id">
                        {stats.minCapexId}
                    </div>
                </div>
            </div>
            <div className="stat-item">
                <div className="stat-info">
                    <div className="stat-label">Min OPEX</div>
                    <div className="stat-value">
                        ${(stats.minOpex / 1000).toFixed(2)}k
                    </div>
                    <div className="stat-value-id">
                        {stats.minOpexId}
                    </div>
                </div>
            </div>
            <div className="stat-item">
                <div className="stat-info">
                    <div className="stat-label">Max Charger Utilization</div>
                    <div className="stat-value">
                        {stats.maxChargerUtil.toFixed(2)}%
                    </div>
                    <div className="stat-value-id">
                        {stats.maxChargerUtilId}
                    </div>
                </div>
            </div>
            <div className="stat-item">
                <div className="stat-info">
                    <div className="stat-label">Max Vehicle Operating Utilization</div>
                    <div className="stat-value">
                        {stats.maxVehicleUtil.toFixed(2)}%
                    </div>
                    <div className="stat-value-id">
                        {stats.maxVehicleUtilId}
                    </div>
                </div>
            </div>
            <div className="stat-item">
                <div className="stat-info">
                    <div className="stat-label">Max CAPEX</div>
                    <div className="stat-value">
                        ${(stats.maxCapex / 1000).toFixed(2)}k
                    </div>
                    <div className="stat-value-id">
                        {stats.maxCapexId}
                    </div>
                </div>
            </div>
            <div className="stat-item">
                <div className="stat-info">
                    <div className="stat-label">Max OPEX</div>
                    <div className="stat-value">
                        ${(stats.maxOpex / 1000).toFixed(2)}k
                    </div>
                    <div className="stat-value-id">
                        {stats.minOpexId}
                    </div>
                </div>
            </div>
            <div className="stat-item">
                <div className="stat-info">
                    <div className="stat-label">Min Charger Utilization</div>
                    <div className="stat-value">
                        {stats.minChargerUtil.toFixed(2)}%
                    </div>
                    <div className="stat-value-id">
                        {stats.minChargerUtilId}
                    </div>
                </div>
            </div>
            <div className="stat-item">
                <div className="stat-info">
                    <div className="stat-label">Min Vehicle Operating Utilization</div>
                    <div className="stat-value">
                        {stats.minVehicleUtil.toFixed(2)}%
                    </div>
                    <div className="stat-value-id">
                        {stats.minVehicleUtilId}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ExperimentsTable;